import React from 'react';
import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ADMIN_COLORS, ADMIN_FONT } from '../../config/theme/adminColors';
import { InfoBanner } from '../../pages/AdminPanel/Products/formStyles';

function FilterCheckbox({ label, checked, disabled, inherited, onToggle }) {
  return (
    <Pressable
      onPress={disabled ? undefined : onToggle}
      style={({ hovered }) => [
        styles.filterRow,
        checked && styles.filterRowActive,
        disabled && styles.filterRowDisabled,
        !disabled && hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
      ]}
    >
      <View
        style={[
          styles.checkbox,
          checked && styles.checkboxChecked,
          disabled && styles.checkboxDisabled,
        ]}
      >
        {checked ? <Ionicons name="checkmark" size={14} color="#fff" /> : null}
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={[styles.filterLabel, disabled && { color: ADMIN_COLORS.muted }]}>
          {label}
        </Text>
        {inherited ? (
          <Text style={styles.inheritedHint}>Herdado da categoria</Text>
        ) : null}
      </View>
    </Pressable>
  );
}

/**
 * Vincula categorias/módulos aos Filtros Inteligentes globais (bidirecional com o painel Filtros).
 */
export default function EntityFilterLinksSection({
  entityLabel = 'item',
  filters = [],
  loading = false,
  prefilterByTagFirst = false,
  selectedSlugs = [],
  inheritedSlugs = [],
  onToggleSlug,
  hasProductCatalog = false,
  productFilterGroups = [],
  productFiltersLoading = false,
  selectedProductFilterKeys = [],
  onToggleProductFilterKey,
  showProductFiltersHint = true,
  compact = false,
}) {
  const inheritedSet = new Set(inheritedSlugs || []);
  const selectedSet = new Set(selectedSlugs || []);
  const selectedProductSet = new Set(selectedProductFilterKeys || []);
  const hasSelection = selectedSlugs.length > 0 || inheritedSlugs.length > 0;
  const showPrefilterWarning = prefilterByTagFirst && !hasSelection;
  const hasLinkedProducts = productFilterGroups.length > 0;

  if (loading) {
    return (
      <View style={styles.loadingBox}>
        <ActivityIndicator size="small" color={ADMIN_COLORS.accent} />
        <Text style={styles.loadingText}>Carregando filtros...</Text>
      </View>
    );
  }

  return (
    <View style={compact ? null : { marginTop: compact ? 0 : 4 }}>
      {!compact ? (
        <>
          <Text style={styles.sectionTitle}>Filtros gerais do app</Text>
          <Text style={styles.sectionHint}>
            Filtros Inteligentes valem para todo o catálogo. Também pode vincular em Filtros →
            categorias/módulos.
          </Text>
        </>
      ) : null}

      {showPrefilterWarning ? (
        <View style={{ marginBottom: 12 }}>
          <InfoBanner variant="warning">
            Pré-filtrar ao abrir o app está ligado: {entityLabel}s sem filtro não aparecem no
            catálogo até serem vinculados aqui ou em Filtros.
          </InfoBanner>
        </View>
      ) : null}

      {filters.length === 0 ? (
        <View style={styles.emptyBox}>
          <Text style={styles.emptyText}>
            Nenhum filtro global ativo. Crie em Filtros (menu superior) para organizar o catálogo.
          </Text>
        </View>
      ) : (
        <View style={styles.filterList}>
          {filters.map((filter) => {
            const inherited = inheritedSet.has(filter.slug);
            const checked = inherited || selectedSet.has(filter.slug);
            return (
              <FilterCheckbox
                key={filter.slug}
                label={filter.name}
                checked={checked}
                inherited={inherited}
                disabled={inherited}
                onToggle={() => onToggleSlug(filter.slug)}
              />
            );
          })}
        </View>
      )}

      {hasProductCatalog ? (
        <View style={[styles.productSection, compact && { marginTop: 12 }]}>
          <Text style={styles.sectionTitle}>Filtros por produto</Text>
          <Text style={styles.sectionHint}>
            Filtros da vitrine do produto vinculado em Acesso ao módulo. Também pode configurar em
            Produtos → Filtros.
          </Text>

          {productFiltersLoading ? (
            <View style={styles.loadingBox}>
              <ActivityIndicator size="small" color={ADMIN_COLORS.accent} />
              <Text style={styles.loadingText}>Carregando filtros por produto...</Text>
            </View>
          ) : !hasLinkedProducts ? (
            <View style={styles.emptyBox}>
              <Text style={styles.emptyText}>
                Vincule planos ou produtos em Acesso ao módulo e configure filtros na vitrine do
                produto para aparecerem aqui.
              </Text>
            </View>
          ) : (
            <View style={styles.productGroupList}>
              {productFilterGroups.map((group) => (
                <View key={group.key} style={styles.productGroup}>
                  <Text style={styles.productGroupTitle}>{group.label}</Text>
                  <View style={styles.filterList}>
                    {group.filters.map((filter) => {
                      const inherited = !!filter.inherited;
                      const checked = inherited || selectedProductSet.has(filter.filterKey);
                      return (
                        <FilterCheckbox
                          key={filter.filterKey}
                          label={filter.name}
                          checked={checked}
                          inherited={inherited}
                          disabled={inherited}
                          onToggle={() => onToggleProductFilterKey?.(filter.filterKey)}
                        />
                      );
                    })}
                  </View>
                </View>
              ))}
            </View>
          )}
        </View>
      ) : showProductFiltersHint ? (
        <View style={[styles.productHintBox, compact && { marginTop: 12 }]}>
          <Text style={styles.productHintTitle}>Filtros por produto</Text>
          <Text style={styles.productHintText}>
            Para filtros dentro de um produto específico (ex.: Meditações, 5 min), ative a Vitrine
            de Produtos e configure na aba Produtos → Filtros do produto.
          </Text>
        </View>
      ) : null}
    </View>
  );
}

const styles = {
  sectionTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: '700',
    color: ADMIN_COLORS.ink,
    marginBottom: 4,
  },
  sectionHint: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    color: ADMIN_COLORS.muted,
    lineHeight: 17,
    marginBottom: 14,
  },
  loadingBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 12,
  },
  loadingText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
  },
  emptyBox: {
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    padding: 12,
  },
  emptyText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
    lineHeight: 18,
  },
  filterList: { gap: 8 },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    cursor: 'pointer',
  },
  filterRowActive: {
    borderColor: ADMIN_COLORS.infoBorder,
    backgroundColor: ADMIN_COLORS.accentSoft,
  },
  filterRowDisabled: {
    cursor: 'default',
    opacity: 0.85,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: ADMIN_COLORS.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ADMIN_COLORS.surface,
  },
  checkboxChecked: {
    backgroundColor: ADMIN_COLORS.accent,
    borderColor: ADMIN_COLORS.accent,
  },
  checkboxDisabled: {
    backgroundColor: ADMIN_COLORS.muted,
    borderColor: ADMIN_COLORS.muted,
  },
  filterLabel: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.ink,
  },
  inheritedHint: {
    fontFamily: ADMIN_FONT,
    fontSize: 11,
    color: ADMIN_COLORS.muted,
    marginTop: 2,
  },
  productHintBox: {
    marginTop: 16,
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
  },
  productHintTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: '700',
    color: ADMIN_COLORS.ink,
    marginBottom: 4,
  },
  productHintText: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    color: ADMIN_COLORS.muted,
    lineHeight: 17,
  },
  productSection: {
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.border,
  },
  productGroupList: { gap: 16 },
  productGroup: { gap: 8 },
  productGroupTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: '700',
    color: ADMIN_COLORS.ink,
  },
};
